import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FaUsers, FaSearch, FaPlus, FaSave, FaTimes,
  FaEdit, FaFilter, FaSync, FaChevronLeft,
  FaChevronRight, FaIdCard, FaMapMarkerAlt,
  FaPhone, FaBuilding, FaEnvelope, FaUser,
  FaHome, FaCity, FaMapPin, FaRoad, FaGlobeAmericas,
  FaCheckCircle, FaTimesCircle, FaSpinner, FaExclamationTriangle
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import InputMask from 'react-input-mask';
import { consultarCnpj, completarComDadosCnpj, avisoCompletarCnpj } from '../others/consultaCnpj';
import ImportarPrintCliente, { ESTILO_CAMPO_DO_PRINT, cepVeioDoPrint } from '../components/ImportarPrintCliente';

function GerenciarClientes() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  // Estados principais
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [modalEdicao, setModalEdicao] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  
  // Estados de filtro
  const [buscaNome, setBuscaNome] = useState('');
  
  // Estados de paginação
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalElementos, setTotalElementos] = useState(0);
  const [itensPorPagina] = useState(12);
  
  // Estado do formulário
  const [formCliente, setFormCliente] = useState({
    nome: '',
    identidade: '',
    cpf: '',
    cnpj: '',
    telefone: '',
    email: '',
    logradouro: '',
    numero: '',
    bairro: '',
    complemento: '',
    cep: '',
    cidade: '',
    uf: '',
    inscricaoMunicipalTomador: ''
  });
  const [errors, setErrors] = useState({});
  // Campos alterados pela leitura do print, destacados em amarelo até o modal ser fechado
  const [camposDoPrint, setCamposDoPrint] = useState([]);
  // CEP preenchido pelo print: enquanto não mudar, sair do campo CEP não busca o endereço na API
  const [cepDoPrint, setCepDoPrint] = useState('');

  // ── NOVO: rótulos amigáveis dos campos, usados pra montar a mensagem do
  // toast de erro de validação (ex: "Telefone: ...", em vez de mostrar só o
  // texto cru do erro sem indicar QUAL campo é). ──
  const rotulosCampos = {
    nome: 'Nome',
    email: 'E-mail',
    cpf: 'CPF',
    cnpj: 'CNPJ',
    telefone: 'Telefone',
    cep: 'CEP',
    logradouro: 'Logradouro',
    numero: 'Número',
    bairro: 'Bairro',
    cidade: 'Cidade',
    uf: 'Estado (UF)',
  };
  
  // Estados para validação visual de CPF e CNPJ
  const [cpfStatus, setCpfStatus] = useState(null);
  const [cnpjStatus, setCnpjStatus] = useState(null);
  const [cpfMessage, setCpfMessage] = useState('');
  const [cnpjMessage, setCnpjMessage] = useState('');

  // Refs
  const isMounted = useRef(false);
  const cpfTimeoutRef = useRef(null);
  const cnpjTimeoutRef = useRef(null);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const addToastRef = useRef(addToast);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  }, []);

  // Validar CPF
  const validarCPF = (cpf) => {
    if (!cpf) return true;
    const cpfLimpo = cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) return false;
    if (/^(\d)\1+$/.test(cpfLimpo)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpfLimpo.charAt(i)) * (10 - i);
    let resto = 11 - (soma % 11);
    let digito1 = resto > 9 ? 0 : resto;
    if (digito1 !== parseInt(cpfLimpo.charAt(9))) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpfLimpo.charAt(i)) * (11 - i);
    resto = 11 - (soma % 11);
    let digito2 = resto > 9 ? 0 : resto;
    if (digito2 !== parseInt(cpfLimpo.charAt(10))) return false;
    return true;
  };

  // Validar CNPJ
  const validarCNPJ = (cnpj) => {
    if (!cnpj) return true;
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) return false;
    if (/^(\d)\1+$/.test(cnpjLimpo)) return false;
    let tamanho = cnpjLimpo.length - 2;
    let numeros = cnpjLimpo.substring(0, tamanho);
    let digitos = cnpjLimpo.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    tamanho = tamanho + 1;
    numeros = cnpjLimpo.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    return true;
  };

  const validarEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validarCEP = (cep) => {
    if (!cep) return true;
    const cepLimpo = cep.replace(/\D/g, '');
    return cepLimpo.length === 8;
  };

  // Verificar CPF na API
  const verificarCpfNaApi = async (cpf) => {
    if (!cpf || cpf.replace(/\D/g, '').length !== 11) return;
    const cpfLimpo = cpf.replace(/\D/g, '');
    if (!validarCPF(cpf)) {
      setCpfStatus('invalid');
      setCpfMessage('CPF inválido');
      setErrors(prev => ({...prev, cpf: 'CPF inválido'}));
      return;
    }
    setCpfStatus('checking');
    setCpfMessage('Verificando CPF...');
    setErrors(prev => { const e = {...prev}; delete e.cpf; return e; });
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/cpf/${cpfLimpo}`, getAuthHeader());
      if (response.data) {
        if (modalEdicao && clienteEditando && response.data.idCliente === clienteEditando.idCliente) {
          setCpfStatus('valid'); setCpfMessage('CPF válido');
        } else {
          setCpfStatus('duplicate'); setCpfMessage('CPF já cadastrado no sistema');
          setErrors(prev => ({...prev, cpf: 'CPF já cadastrado no sistema'}));
        }
      } else { setCpfStatus('valid'); setCpfMessage('CPF válido'); }
    } catch (error) {
      if (error.response && (error.response.status === 204 || error.response.status === 404)) {
        setCpfStatus('valid'); setCpfMessage('CPF válido');
      } else { setCpfStatus('valid'); setCpfMessage('CPF válido'); }
    }
  };

  // Verificar CNPJ na API
  const verificarCnpjNaApi = async (cnpj) => {
    if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) return;
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (!validarCNPJ(cnpj)) {
      setCnpjStatus('invalid'); setCnpjMessage('CNPJ inválido');
      setErrors(prev => ({...prev, cnpj: 'CNPJ inválido'}));
      return;
    }
    setCnpjStatus('checking'); setCnpjMessage('Verificando CNPJ...');
    setErrors(prev => { const e = {...prev}; delete e.cnpj; return e; });
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpjLimpo}`, getAuthHeader());
      if (response.data) {
        if (modalEdicao && clienteEditando && response.data.idCliente === clienteEditando.idCliente) {
          setCnpjStatus('valid'); setCnpjMessage('CNPJ válido');
        } else {
          setCnpjStatus('duplicate'); setCnpjMessage('CNPJ já cadastrado no sistema');
          setErrors(prev => ({...prev, cnpj: 'CNPJ já cadastrado no sistema'}));
        }
      } else { setCnpjStatus('valid'); setCnpjMessage('CNPJ válido'); }
    } catch (error) {
      if (error.response && (error.response.status === 204 || error.response.status === 404)) {
        setCnpjStatus('valid'); setCnpjMessage('CNPJ válido');
      } else { setCnpjStatus('valid'); setCnpjMessage('CNPJ válido'); }
    }
  };

  const handleCpfChange = (e) => {
    const value = e.target.value;
    setFormCliente({...formCliente, cpf: value});
    if (cpfTimeoutRef.current) clearTimeout(cpfTimeoutRef.current);
    const cpfLimpo = value.replace(/\D/g, '');
    if (cpfLimpo.length === 0) {
      setCpfStatus(null); setCpfMessage('');
      setErrors(prev => { const e = {...prev}; delete e.cpf; return e; });
      return;
    }
    if (cpfLimpo.length === 11) {
      cpfTimeoutRef.current = setTimeout(() => verificarCpfNaApi(value), 800);
    } else {
      setCpfStatus(null); setCpfMessage('');
      setErrors(prev => { const e = {...prev}; delete e.cpf; return e; });
    }
  };

  // A lupa habilita com CNPJ completo e válido (no cadastro novo, ainda não cadastrado; na edição, o do próprio cliente)
  const podeConsultarCnpj = (formCliente.cnpj || '').replace(/\D/g, '').length === 14
    && cnpjStatus === 'valid'
    && !loading
    && !buscandoCnpj;

  // Preenche os dados do cliente a partir do CNPJ digitado, ao clicar na lupa.
  // Substitui nome, contato e endereço pelos do CNPJ consultado (inclusive limpando o que a
  // consulta não trouxe), para não sobrar dado de um CNPJ consultado antes.
  const preencherPorCnpj = async () => {
    if (!podeConsultarCnpj) return;
    setBuscandoCnpj(true);
    const { dados, erro } = await consultarCnpj(formCliente.cnpj, API_BASE_URL, getAuthHeader());
    setBuscandoCnpj(false);

    if (!dados) {
      addToastRef.current('error', erro);
      return;
    }

    // Edição (cadastro antigo/incompleto): só completa o que está em branco, sem trocar o que já foi preenchido
    if (modalEdicao) {
      const { novo, completados } = completarComDadosCnpj(formCliente, dados);
      setFormCliente(novo);
      const aviso = avisoCompletarCnpj(completados);
      addToastRef.current(aviso.tipo, aviso.texto);
      return;
    }

    setFormCliente(prev => {
      const novo = { ...prev };
      Object.keys(dados).forEach(campo => {
        novo[campo] = dados[campo] || '';
      });
      return novo;
    });
    const camposOpcionais = {
      email: 'e-mail', telefone: 'telefone', cep: 'CEP', logradouro: 'endereço', bairro: 'bairro', cidade: 'cidade',
    };
    const faltando = Object.keys(camposOpcionais).filter(campo => !dados[campo]).map(campo => camposOpcionais[campo]);
    if (faltando.length > 0) {
      addToastRef.current('warning', `Dados preenchidos pelo CNPJ. Não encontrei: ${faltando.join(', ')}. Preencha manualmente.`);
    } else {
      addToastRef.current('success', 'Dados preenchidos a partir do CNPJ.');
    }
  };

  // Preenche o formulário com os dados lidos do print colado (Ctrl+V). Sobrescreve os campos lidos, inclusive na
  // edição, e destaca os que mudaram de valor; os que o print não trouxe ficam como estão. Nada é salvo sozinho.
  const aplicarDadosDoPrint = (dados, alterados) => {
    setFormCliente(prev => ({ ...prev, ...dados }));
    setCamposDoPrint(prev => [...new Set([...prev, ...alterados])]);
    if (dados.cep) setCepDoPrint(dados.cep);
    setErrors(prev => {
      const e = { ...prev };
      Object.keys(dados).forEach(campo => delete e[campo]);
      return e;
    });
    // Mesma verificação de duplicidade de quando o CPF/CNPJ é digitado
    if (dados.cpf) verificarCpfNaApi(dados.cpf);
    if (dados.cnpj) verificarCnpjNaApi(dados.cnpj);
  };

  const destaquePrint = (campo) => (camposDoPrint.includes(campo) ? ESTILO_CAMPO_DO_PRINT : {});

  const handleCnpjChange = (e) => {
    const value = e.target.value;
    setFormCliente({...formCliente, cnpj: value});
    if (cnpjTimeoutRef.current) clearTimeout(cnpjTimeoutRef.current);
    const cnpjLimpo = value.replace(/\D/g, '');
    if (cnpjLimpo.length === 0) {
      setCnpjStatus(null); setCnpjMessage('');
      setErrors(prev => { const e = {...prev}; delete e.cnpj; return e; });
      return;
    }
    if (cnpjLimpo.length === 14) {
      cnpjTimeoutRef.current = setTimeout(() => verificarCnpjNaApi(value), 800);
    } else {
      setCnpjStatus(null); setCnpjMessage('');
      setErrors(prev => { const e = {...prev}; delete e.cnpj; return e; });
    }
  };

  const handleTelefoneChange = (e) => {
    setFormCliente({ ...formCliente, telefone: e.target.value });
    // ── NOVO: limpa o erro de telefone assim que o campo muda — se o
    // usuário estava vendo o erro "Telefone deve ter..." e continuou
    // digitando, o erro antigo não fica "grudado" mostrando uma mensagem
    // desatualizada até o próximo clique em Salvar. ──
    setErrors(prev => {
      if (!prev.telefone) return prev;
      const e2 = { ...prev };
      delete e2.telefone;
      return e2;
    });
  };

  // Buscar endereço pelo CEP
  const buscarEnderecoPorCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    try {
      setLoading(true);
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data && !response.data.erro) {
        setFormCliente(prev => ({
          ...prev,
          logradouro: response.data.logradouro || '',
          bairro: response.data.bairro || '',
          cidade: response.data.localidade || '',
          uf: response.data.uf || '',
          complemento: response.data.complemento || prev.complemento
        }));
        addToastRef.current('success', 'Endereço encontrado pelo CEP!');
      } else {
        addToastRef.current('warning', 'CEP não encontrado');
      }
    } catch (error) {
      addToastRef.current('error', 'Erro ao buscar endereço pelo CEP');
    } finally {
      setLoading(false);
    }
  };

  // ── CORREÇÃO: antes, essa função só retornava true/false (o objeto de
  // erros ficava só no state `errors`, que demora um ciclo de render pra
  // atualizar). Isso significa que, ao clicar em Salvar, era impossível
  // saber IMEDIATAMENTE qual campo tinha travado o formulário sem rolar a
  // tela procurando o texto vermelho — e se o campo com erro estivesse fora
  // da área visível (ex: Telefone incompleto, enquanto o usuário já tinha
  // rolado até Endereço), parecia que o sistema simplesmente "não salvava"
  // sem motivo aparente. Agora a função retorna o objeto de erros
  // encontrados, e quem chama pode montar uma mensagem clara na hora. ──
  const validarFormulario = async () => {
    const newErrors = {};
    if (!formCliente.nome || formCliente.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formCliente.nome.length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    }
    if (formCliente.email && formCliente.email.trim() !== '') {
      if (!validarEmail(formCliente.email)) newErrors.email = 'Email inválido';
    }
    const cpfLimpo = (formCliente.cpf || '').replace(/\D/g, '');
    const cnpjLimpo = (formCliente.cnpj || '').replace(/\D/g, '');
    if (cpfLimpo === '' && cnpjLimpo === '') {
      newErrors.cpf = 'CPF ou CNPJ é obrigatório';
      newErrors.cnpj = 'CPF ou CNPJ é obrigatório';
    } else {
      if (cpfLimpo !== '') {
        if (cpfLimpo.length !== 11) newErrors.cpf = 'CPF deve ter 11 dígitos';
        else if (!validarCPF(formCliente.cpf)) newErrors.cpf = 'CPF inválido';
        else if (cpfStatus === 'duplicate') newErrors.cpf = 'CPF já cadastrado no sistema';
      }
      if (cnpjLimpo !== '') {
        if (cnpjLimpo.length !== 14) newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
        else if (!validarCNPJ(formCliente.cnpj)) newErrors.cnpj = 'CNPJ inválido';
        else if (cnpjStatus === 'duplicate') newErrors.cnpj = 'CNPJ já cadastrado no sistema';
      }
    }
    if (formCliente.telefone && formCliente.telefone.replace(/\D/g, '') !== '') {
      const telefoneLimpo = formCliente.telefone.replace(/\D/g, '');
      // Aceita 10 dígitos (fixo: DDD + 8) ou 11 dígitos (celular: DDD + 9)
      if (telefoneLimpo.length !== 10 && telefoneLimpo.length !== 11) {
        newErrors.telefone = 'Telefone deve ter DDD + 8 dígitos (fixo) ou DDD + 9 dígitos (celular)';
      }
    }
    // ── Endereço obrigatório (somente validação de front - o backend segue
    // aceitando esses campos em branco): logradouro/bairro/cidade/UF/CEP
    // agora seguem a mesma lógica de campo obrigatório já usada acima pro
    // Nome, sem validação de formato além do próprio CEP (que já validava
    // o formato quando preenchido). Número fica de fora - não é obrigatório. ──
    if (!formCliente.logradouro || formCliente.logradouro.trim() === '') {
      newErrors.logradouro = 'Logradouro é obrigatório';
    }
    if (!formCliente.bairro || formCliente.bairro.trim() === '') {
      newErrors.bairro = 'Bairro é obrigatório';
    }
    if (!formCliente.cidade || formCliente.cidade.trim() === '') {
      newErrors.cidade = 'Cidade é obrigatória';
    }
    if (!formCliente.uf || formCliente.uf.trim() === '') {
      newErrors.uf = 'Estado (UF) é obrigatório';
    }
    if (!formCliente.cep || formCliente.cep.replace(/\D/g, '') === '') {
      newErrors.cep = 'CEP é obrigatório';
    } else if (!validarCEP(formCliente.cep)) {
      newErrors.cep = 'CEP inválido - deve ter 8 dígitos';
    }
    setErrors(newErrors);
    return newErrors;
  };

  // Buscar clientes
  const buscarClientes = useCallback(async (page = paginaAtual, search = buscaNome) => {
    if (!isMounted.current) return;
    setLoading(true);
    try {
      let url;
      if (search && search.trim() !== '') {
        // CPF/CNPJ (inteiro ou parte, com ou sem pontuação) vai só com os dígitos: a "/" do CNPJ quebraria a URL.
        // O back busca no documento quando o termo é só número; texto continua buscando pelo nome.
        const termo = /^[\d.\-/\s]+$/.test(search.trim()) ? search.replace(/\D/g, '') : search.trim();
        url = `${API_BASE_URL}/clientes/nome/${encodeURIComponent(termo)}/pagina?page=${page}&size=${itensPorPagina}`;
      } else {
        url = `${API_BASE_URL}/clientes/nome/pagina?page=${page}&size=${itensPorPagina}`;
      }
      const response = await axios.get(url, getAuthHeader());
      if (isMounted.current) {
        if (response.data) {
          setClientes(response.data.content || []);
          setTotalPaginas(response.data.totalPages || 0);
          setTotalElementos(response.data.totalElements || 0);
        } else {
          setClientes([]); setTotalPaginas(0); setTotalElementos(0);
        }
      }
    } catch (error) {
      if (isMounted.current) {
        if (error.response && error.response.status === 404) {
          setClientes([]); setTotalPaginas(0); setTotalElementos(0);
        } else {
          addToastRef.current('error', 'Erro ao carregar clientes');
          setClientes([]); setTotalPaginas(0); setTotalElementos(0);
        }
      }
    } finally {
      if (isMounted.current) { setLoading(false); setInitialLoading(false); }
    }
  }, [API_BASE_URL, itensPorPagina, getAuthHeader]);

  useEffect(() => {
    isMounted.current = true;
    buscarClientes(paginaAtual, buscaNome);
    return () => {
      isMounted.current = false;
      if (cpfTimeoutRef.current) clearTimeout(cpfTimeoutRef.current);
      if (cnpjTimeoutRef.current) clearTimeout(cnpjTimeoutRef.current);
    };
  }, [paginaAtual, buscaNome, buscarClientes]);

  const aplicarFiltro = () => setPaginaAtual(0);
  const limparFiltro = () => { setBuscaNome(''); setPaginaAtual(0); };

  // ── CORREÇÃO: `validarFormulario` agora devolve o objeto de erros na
  // hora — sem depender do state `errors` (que só reflete a mudança no
  // próximo render). Se houver qualquer erro, mostramos um toast dizendo
  // EXATAMENTE quais campos estão bloqueando o salvamento, mesmo que esses
  // campos estejam fora da área visível da tela no momento (ex: Telefone
  // incompleto enquanto o usuário está olhando pra seção de Endereço). Isso
  // resolve o "trava sem motivo aparente" — agora sempre aparece um aviso
  // claro apontando o campo certo. ──
  const salvarCliente = async () => {
    const validationErrors = await validarFormulario();
    if (Object.keys(validationErrors).length > 0) {
      const mensagens = Object.entries(validationErrors)
        .map(([campo, msg]) => `${rotulosCampos[campo] || campo}: ${msg}`)
        .join(' | ');
      addToastRef.current('warning', `Corrija antes de salvar → ${mensagens}`);
      return;
    }
    setLoading(true);
    try {
      const dadosCliente = {
        nome: formCliente.nome,
        identidade: formCliente.identidade || null,
        cpf: formCliente.cpf || null,
        cnpj: formCliente.cnpj || null,
        telefone: formCliente.telefone || null,
        email: formCliente.email || null,
        logradouro: formCliente.logradouro || null,
        numero: formCliente.numero || null,
        bairro: formCliente.bairro || null,
        complemento: formCliente.complemento || null,
        cep: formCliente.cep || null,
        cidade: formCliente.cidade || null,
        uf: formCliente.uf || null,
        inscricaoMunicipalTomador: formCliente.inscricaoMunicipalTomador || null
      };
      if (modalEdicao && clienteEditando) {
        await axios.put(`${API_BASE_URL}/clientes/${clienteEditando.idCliente}`, dadosCliente, getAuthHeader());
        addToastRef.current('success', 'Cliente atualizado com sucesso!');
      } else {
        await axios.post(`${API_BASE_URL}/clientes`, dadosCliente, getAuthHeader());
        addToastRef.current('success', 'Cliente adicionado com sucesso!');
      }
      fecharModal();
      buscarClientes(0, buscaNome);
    } catch (error) {
      // ── CORREÇÃO: antes, se o backend recusasse o salvamento (ex: erro de
      // validação no banco, campo grande demais, etc.) e não mandasse um
      // `message` no corpo da resposta, o toast caía no `error.message`
      // genérico do axios (ex: "Request failed with status code 500"), o
      // que não ajuda a identificar o campo problemático. Agora, se não
      // vier mensagem específica do backend, avisamos que pode ser algum
      // campo do endereço com texto muito longo ou caractere inválido —
      // pista mais útil do que o erro cru do axios. ──
      const mensagemBackend = error.response?.data?.message;
      addToastRef.current(
        'error',
        mensagemBackend
          ? `Erro ao salvar cliente: ${mensagemBackend}`
          : `Erro ao salvar cliente (${error.response?.status || 'sem resposta do servidor'}). Verifique se algum campo do endereço está muito longo ou com caractere inválido.`
      );
    } finally {
      setLoading(false);
    }
  };

  // Form reset helper
  const formVazio = () => ({
    nome: '',
    identidade: '',
    cpf: '',
    cnpj: '',
    telefone: '',
    email: '',
    logradouro: '',
    numero: '',
    bairro: '',
    complemento: '',
    cep: '',
    cidade: '',
    uf: '',
    inscricaoMunicipalTomador: ''
  });

  const abrirModalNovo = () => {
    setModalEdicao(false);
    setClienteEditando(null);
    setFormCliente(formVazio());
    setErrors({});
    setCamposDoPrint([]);
    setCepDoPrint('');
    setCpfStatus(null); setCnpjStatus(null);
    setCpfMessage(''); setCnpjMessage('');
    setModalAberto(true);
  };

  const abrirModalEditar = (cliente) => {
    setModalEdicao(true);
    setClienteEditando(cliente);
    setFormCliente({
      nome: cliente.nome || '',
      identidade: cliente.identidade || '',
      cpf: cliente.cpf || '',
      cnpj: cliente.cnpj || '',
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      logradouro: cliente.logradouro || '',
      numero: cliente.numero || '',
      bairro: cliente.bairro || '',
      complemento: cliente.complemento || '',
      cep: cliente.cep || '',
      cidade: cliente.cidade || '',
      uf: cliente.uf || '',
      inscricaoMunicipalTomador: cliente.inscricaoMunicipalTomador || ''
    });
    setErrors({});
    setCamposDoPrint([]);
    setCepDoPrint('');
    if (cliente.cpf) { setCpfStatus('valid'); setCpfMessage('CPF já cadastrado'); }
    else { setCpfStatus(null); setCpfMessage(''); }
    if (cliente.cnpj) { setCnpjStatus('valid'); setCnpjMessage('CNPJ já cadastrado'); }
    else { setCnpjStatus(null); setCnpjMessage(''); }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setModalEdicao(false);
    setClienteEditando(null);
    setFormCliente(formVazio());
    setErrors({});
    setCamposDoPrint([]);
    setCepDoPrint('');
    setCpfStatus(null); setCnpjStatus(null);
    setCpfMessage(''); setCnpjMessage('');
  };

  const mudarPagina = (novaPagina) => {
    if (novaPagina >= 0 && novaPagina < totalPaginas) setPaginaAtual(novaPagina);
  };

  const formatarCpfCnpj = (valor) => {
    if (!valor) return null;
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length === 11) return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (numeros.length === 14) return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return valor;
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return null;
    const numeros = telefone.replace(/\D/g, '');
    if (numeros.length === 11) return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (numeros.length === 10) return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return telefone;
  };

  const formatarCEP = (cep) => {
    if (!cep) return null;
    const numeros = cep.replace(/\D/g, '');
    if (numeros.length === 8) return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
    return cep;
  };

  const montarEnderecoCompleto = (cliente) => {
    if (!cliente.logradouro && !cliente.numero && !cliente.bairro) {
      return cliente.cep ? `CEP: ${formatarCEP(cliente.cep)}` : null;
    }
    let endereco = '';
    if (cliente.logradouro) endereco += cliente.logradouro;
    if (cliente.numero) endereco += endereco ? `, ${cliente.numero}` : cliente.numero;
    if (cliente.bairro) endereco += endereco ? ` - ${cliente.bairro}` : cliente.bairro;
    if (cliente.cidade) endereco += endereco ? `, ${cliente.cidade}` : cliente.cidade;
    if (cliente.uf) endereco += endereco ? `/${cliente.uf}` : cliente.uf;
    return endereco || null;
  };

  const getInputBorderStyle = (status, hasError) => {
    if (hasError) return { borderColor: '#e74c3c', boxShadow: '0 0 0 3px rgba(231,76,60,0.1)' };
    if (status === 'checking') return { borderColor: '#f39c12', boxShadow: '0 0 0 3px rgba(243,156,18,0.1)' };
    if (status === 'valid') return { borderColor: '#27ae60', boxShadow: '0 0 0 3px rgba(39,174,96,0.1)' };
    if (status === 'invalid' || status === 'duplicate') return { borderColor: '#e74c3c', boxShadow: '0 0 0 3px rgba(231,76,60,0.1)' };
    return {};
  };

  // ── NOVO: quantidade de erros atuais, usada pra decidir se mostra o
  // banner de resumo de erros no topo do formulário do modal ──
  const totalErros = Object.keys(errors).length;

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <div style={styles.titleContainer}>
            <FaUsers style={styles.titleIcon} />
            <h1 style={styles.title}>CADASTRO DE CLIENTES</h1>
          </div>
        </div>

        <div style={styles.formContainer}>
          {/* Filtros */}
          <div style={styles.filtrosContainer}>
            <div style={styles.filtrosWrapper}>
              <div style={styles.filtroGroupBusca}>
                <label style={styles.filtroLabel}>
                  <FaSearch style={styles.filtroIcon} />
                  Buscar por nome ou CPF/CNPJ
                </label>
                <input
                  type="text"
                  value={buscaNome}
                  onChange={(e) => setBuscaNome(e.target.value)}
                  placeholder="Digite o nome, CPF ou CNPJ do cliente..."
                  style={styles.filtroInputBusca}
                  onKeyPress={(e) => e.key === 'Enter' && aplicarFiltro()}
                />
              </div>
              <div style={styles.filtroActions}>
                <button onClick={aplicarFiltro} style={styles.filtrarButton} disabled={loading}>
                  <FaFilter /> FILTRAR
                </button>
                <button onClick={limparFiltro} style={styles.limparButton} disabled={loading}>
                  <FaSync /> LIMPAR
                </button>
                <button onClick={abrirModalNovo} style={styles.addButtonInline} disabled={loading}>
                  <FaPlus /> ADICIONAR
                </button>
              </div>
            </div>
          </div>

          {/* Cards de Clientes */}
          <div style={styles.cardsContainer}>
            {initialLoading ? (
              <div style={styles.loading}>Carregando clientes...</div>
            ) : loading && clientes.length === 0 ? (
              <div style={styles.loading}>Buscando clientes...</div>
            ) : clientes.length > 0 ? (
              <>
                <div style={styles.cardsGrid}>
                  {clientes.map((cliente) => (
                    <div key={cliente.idCliente} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <div style={styles.cardIconContainer}>
                          <FaUser style={styles.cardUserIcon} />
                        </div>
                        <div style={styles.cardTitle}>
                          <h3 style={styles.cardName}>{cliente.nome}</h3>
                          <span style={styles.cardId}>ID: {cliente.idCliente}</span>
                        </div>
                        <button
                          onClick={() => abrirModalEditar(cliente)}
                          style={styles.cardEditButton}
                          disabled={loading}
                          title="Editar cliente"
                        >
                          <FaEdit />
                        </button>
                      </div>

                      <div style={styles.cardBody}>
                        {cliente.identidade && (
                          <div style={styles.cardInfo}>
                            <FaIdCard style={styles.cardInfoIcon} />
                            <span style={styles.cardInfoText}>RG: {cliente.identidade}</span>
                          </div>
                        )}
                        {cliente.email && (
                          <div style={styles.cardInfo}>
                            <FaEnvelope style={styles.cardInfoIcon} />
                            <span style={styles.cardInfoText} title={cliente.email}>{cliente.email}</span>
                          </div>
                        )}
                        {cliente.telefone && formatarTelefone(cliente.telefone) && (
                          <div style={styles.cardInfo}>
                            <FaPhone style={styles.cardInfoIcon} />
                            <span style={styles.cardInfoText}>{formatarTelefone(cliente.telefone)}</span>
                          </div>
                        )}
                        {cliente.cpf && formatarCpfCnpj(cliente.cpf) && (
                          <div style={styles.cardInfo}>
                            <FaIdCard style={styles.cardInfoIcon} />
                            <span style={styles.cardInfoText}>CPF: {formatarCpfCnpj(cliente.cpf)}</span>
                          </div>
                        )}
                        {cliente.cnpj && formatarCpfCnpj(cliente.cnpj) && (
                          <div style={styles.cardInfo}>
                            <FaBuilding style={styles.cardInfoIcon} />
                            <span style={styles.cardInfoText}>CNPJ: {formatarCpfCnpj(cliente.cnpj)}</span>
                          </div>
                        )}
                        {montarEnderecoCompleto(cliente) && (
                          <div style={styles.cardInfo}>
                            <FaMapMarkerAlt style={styles.cardInfoIcon} />
                            <span style={styles.cardInfoText} title={montarEnderecoCompleto(cliente)}>
                              {montarEnderecoCompleto(cliente).length > 45
                                ? `${montarEnderecoCompleto(cliente).substring(0, 45)}...`
                                : montarEnderecoCompleto(cliente)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Paginação */}
                {totalPaginas > 1 && (
                  <div style={styles.pagination}>
                    <button
                      onClick={() => mudarPagina(paginaAtual - 1)}
                      disabled={paginaAtual === 0 || loading}
                      style={styles.paginationButton}
                    >
                      <FaChevronLeft />
                    </button>
                    <span style={styles.paginationInfo}>
                      Página {paginaAtual + 1} de {totalPaginas}
                      {totalElementos > 0 && ` (${totalElementos} clientes)`}
                    </span>
                    <button
                      onClick={() => mudarPagina(paginaAtual + 1)}
                      disabled={paginaAtual === totalPaginas - 1 || loading}
                      style={styles.paginationButton}
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.noResults}>
                <FaUsers style={styles.noResultsIcon} />
                <p>Nenhum cliente encontrado</p>
                <small>Clique em "ADICIONAR" para começar</small>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Adicionar/Editar Cliente */}
        {modalAberto && (
          <div style={styles.modalOverlay}>
            <div style={styles.modal}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>
                  <FaUsers style={styles.modalTitleIcon} />
                  {modalEdicao ? 'Editar Cliente' : 'Adicionar Cliente'}
                </h2>
                <button onClick={fecharModal} style={styles.modalClose}>
                  <FaTimes />
                </button>
              </div>

              <div style={styles.modalContent}>
                {/* ── NOVO: banner de resumo de erros — fica logo no topo do
                    conteúdo do modal, então aparece mesmo que o usuário tenha
                    rolado a tela pra uma seção mais abaixo (ex: Endereço). Some
                    sozinho assim que todos os erros forem corrigidos. ── */}
                {totalErros > 0 && (
                  <div style={styles.erroResumoBanner}>
                    <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <strong>Corrija os campos abaixo antes de salvar:</strong>
                      <ul style={styles.erroResumoLista}>
                        {Object.entries(errors).map(([campo, msg]) => (
                          <li key={campo}>{rotulosCampos[campo] || campo}: {msg}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Colar print (Ctrl+V) com os dados do cliente: lê por OCR e preenche os campos */}
                <ImportarPrintCliente valoresAtuais={formCliente} onDadosLidos={aplicarDadosDoPrint} disabled={loading} />

                <div style={styles.modalForm}>

                  {/* ── Informações Pessoais ── */}
                  <div style={styles.sectionTitle}>
                    <FaUser style={styles.sectionIcon} />
                    Informações Pessoais
                  </div>

                  {/* Nome */}
                  <div style={styles.inputGroup}>
                    <label style={styles.labelRequired}>Nome *</label>
                    <input
                      type="text"
                      value={formCliente.nome}
                      onChange={(e) => setFormCliente({...formCliente, nome: e.target.value})}
                      placeholder="Digite o nome completo"
                      style={{...styles.input, ...destaquePrint('nome')}}
                      autoFocus
                    />
                    {errors.nome && <span style={styles.errorText}>{errors.nome}</span>}
                  </div>

                  {/* RG / Identidade */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      <FaIdCard style={styles.labelIcon} />
                      RG / Identidade
                    </label>
                    <input
                      type="text"
                      value={formCliente.identidade}
                      onChange={(e) => setFormCliente({...formCliente, identidade: e.target.value.toUpperCase()})}
                      placeholder="Número do RG ou documento de identidade"
                      style={styles.input}
                    />
                    {errors.identidade && <span style={styles.errorText}>{errors.identidade}</span>}
                    <small style={styles.fieldHint}>Usado para preenchimento automático dos requerimentos do DETRAN</small>
                  </div>

                  {/* Email */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      <FaEnvelope style={styles.labelIcon} />
                      Email
                    </label>
                    <input
                      type="email"
                      value={formCliente.email}
                      onChange={(e) => setFormCliente({...formCliente, email: e.target.value})}
                      placeholder="exemplo@email.com"
                      style={{...styles.input, ...destaquePrint('email')}}
                    />
                    {errors.email && <span style={styles.errorText}>{errors.email}</span>}
                  </div>

                  {/* CPF + CNPJ */}
                  <div style={styles.rowInputs}>
                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        <FaIdCard style={styles.labelIcon} />
                        CPF <span style={{color: '#e74c3c'}}>*</span>
                      </label>
                      <div style={{position: 'relative'}}>
                        <InputMask
                          mask="999.999.999-99"
                          value={formCliente.cpf}
                          onChange={handleCpfChange}
                          placeholder="000.000.000-00"
                          style={{...styles.input, ...destaquePrint('cpf'), ...getInputBorderStyle(cpfStatus, errors.cpf)}}
                        />
                        {cpfStatus === 'checking' && (
                          <FaSpinner className="spin" style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#f39c12', fontSize: '16px'}} />
                        )}
                        {cpfStatus === 'valid' && (
                          <FaCheckCircle style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#27ae60', fontSize: '16px'}} />
                        )}
                        {(cpfStatus === 'invalid' || cpfStatus === 'duplicate') && (
                          <FaTimesCircle style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#e74c3c', fontSize: '16px'}} />
                        )}
                      </div>
                      {cpfMessage && (
                        <small style={{
                          fontSize: '11px', marginTop: '4px',
                          color: cpfStatus === 'valid' ? '#27ae60' : cpfStatus === 'checking' ? '#f39c12' : '#e74c3c',
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          {cpfMessage}
                        </small>
                      )}
                      {errors.cpf && <span style={styles.errorText}>{errors.cpf}</span>}
                      <small style={styles.fieldHint}>CPF é único no sistema — CPF ou CNPJ é obrigatório</small>
                    </div>

                    <div style={styles.inputGroupHalf}>
                      <label style={styles.label}>
                        <FaBuilding style={styles.labelIcon} />
                        CNPJ <span style={{color: '#e74c3c'}}>*</span>
                      </label>
                      <div style={styles.cnpjRow}>
                      <div style={{position: 'relative', flex: 1, minWidth: 0}}>
                        <InputMask
                          mask="99.999.999/9999-99"
                          value={formCliente.cnpj}
                          onChange={handleCnpjChange}
                          placeholder="00.000.000/0000-00"
                          style={{...styles.input, ...destaquePrint('cnpj'), ...getInputBorderStyle(cnpjStatus, errors.cnpj)}}
                        />
                        {cnpjStatus === 'checking' && (
                          <FaSpinner className="spin" style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#f39c12', fontSize: '16px'}} />
                        )}
                        {cnpjStatus === 'valid' && (
                          <FaCheckCircle style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#27ae60', fontSize: '16px'}} />
                        )}
                        {(cnpjStatus === 'invalid' || cnpjStatus === 'duplicate') && (
                          <FaTimesCircle style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#e74c3c', fontSize: '16px'}} />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={preencherPorCnpj}
                        disabled={!podeConsultarCnpj}
                        title={podeConsultarCnpj
                          ? (modalEdicao ? 'Completar os campos em branco com os dados do CNPJ (Receita Federal)' : 'Buscar dados do CNPJ (Receita Federal)')
                          : 'Digite um CNPJ válido para buscar os dados'}
                        style={{...styles.cnpjButton, ...(!podeConsultarCnpj && styles.cnpjButtonDisabled)}}
                      >
                        {buscandoCnpj ? <FaSpinner className="spin" /> : <FaSearch />}
                      </button>
                      </div>
                      {buscandoCnpj && <small style={styles.fieldHint}>Consultando CNPJ...</small>}
                      {cnpjMessage && (
                        <small style={{
                          fontSize: '11px', marginTop: '4px',
                          color: cnpjStatus === 'valid' ? '#27ae60' : cnpjStatus === 'checking' ? '#f39c12' : '#e74c3c',
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          {cnpjMessage}
                        </small>
                      )}
                      {errors.cnpj && <span style={styles.errorText}>{errors.cnpj}</span>}
                      <small style={styles.fieldHint}>CNPJ é único no sistema — CPF ou CNPJ é obrigatório</small>
                    </div>
                  </div>

                  {/* Inscrição Municipal do Tomador — so preciso no raro caso do proprio
                      cliente tambem ser cadastrado como Contribuinte no municipio com mais
                      de um registro (ex.: erro real do Cidade360 "P4016 - Necessario
                      especificar a Inscricao Municipal do Tomador pois o mesmo possui mais
                      de um Contribuinte no municipio"). Fica vazio pra quase todo cliente -
                      quando vazio, a tag <IM> nem e enviada na NFS-e (ver NfseService/
                      NfseCidade360Service). */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      <FaIdCard style={styles.labelIcon} />
                      Inscrição Municipal do Tomador
                    </label>
                    <input
                      type="text"
                      value={formCliente.inscricaoMunicipalTomador}
                      onChange={(e) => setFormCliente({...formCliente, inscricaoMunicipalTomador: e.target.value})}
                      placeholder="Deixe em branco, salvo se a NFS-e for rejeitada pedindo isso"
                      style={styles.input}
                    />
                    {errors.inscricaoMunicipalTomador && <span style={styles.errorText}>{errors.inscricaoMunicipalTomador}</span>}
                    <small style={styles.fieldHint}>
                      Só preencha se a emissão de NFS-e for rejeitada com erro pedindo a "Inscrição Municipal do Tomador"
                      (cliente também cadastrado como Contribuinte no município)
                    </small>
                  </div>

                  {/* Telefone — máscara dinâmica: fixo (8 dígitos) ou celular (9 dígitos) */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      <FaPhone style={styles.labelIcon} />
                      Telefone
                    </label>
                    <InputMask
                      mask="(99) 99999-9999"
                      maskChar={null}
                      value={formCliente.telefone}
                      onChange={handleTelefoneChange}
                      placeholder="(00) 0000-0000 ou (00) 00000-0000"
                      style={{...styles.input, ...destaquePrint('telefone'), ...getInputBorderStyle(null, errors.telefone)}}
                    />
                    {errors.telefone && <span style={styles.errorText}>{errors.telefone}</span>}
                    <small style={styles.fieldHint}>Fixo: (DDD) NNNN-NNNN &nbsp;|&nbsp; Celular: (DDD) 9NNNN-NNNN — deixe vazio se não quiser informar</small>
                  </div>

                  {/* ── Endereço ── */}
                  <div style={styles.sectionDivider}></div>
                  <div style={styles.sectionTitle}>
                    <FaMapMarkerAlt style={styles.sectionIcon} />
                    Endereço
                  </div>

                  {/* Linha 1: CEP | Cidade | UF */}
                  <div style={styles.rowInputs}>
                    <div style={{...styles.inputGroupHalf, flex: 1}}>
                      <label style={styles.label}>
                        <FaMapPin style={styles.labelIcon} />
                        CEP <span style={{color: '#e74c3c'}}>*</span>
                      </label>
                      <InputMask
                        mask="99999-999"
                        value={formCliente.cep}
                        onChange={(e) => setFormCliente({...formCliente, cep: e.target.value})}
                        onBlur={(e) => {
                          if (cepVeioDoPrint(e.target.value, cepDoPrint)) return;
                          const cepLimpo = e.target.value.replace(/\D/g, '');
                          if (cepLimpo.length === 8) buscarEnderecoPorCEP(e.target.value);
                        }}
                        placeholder="00000-000"
                        style={{...styles.input, ...destaquePrint('cep'), ...getInputBorderStyle(null, errors.cep)}}
                      />
                      {errors.cep && <span style={styles.errorText}>{errors.cep}</span>}
                      <small style={styles.fieldHint}>Digite o CEP para buscar o endereço</small>
                    </div>

                    <div style={{...styles.inputGroupHalf, flex: 2}}>
                      <label style={styles.label}>
                        <FaCity style={styles.labelIcon} />
                        Cidade <span style={{color: '#e74c3c'}}>*</span>
                      </label>
                      <input
                        type="text"
                        value={formCliente.cidade}
                        onChange={(e) => setFormCliente({...formCliente, cidade: e.target.value})}
                        placeholder="Cidade"
                        style={{...styles.input, ...destaquePrint('cidade'), ...getInputBorderStyle(null, errors.cidade)}}
                      />
                      {errors.cidade && <span style={styles.errorText}>{errors.cidade}</span>}
                    </div>

                    <div style={{...styles.inputGroupHalf, flex: 0.6}}>
                      <label style={styles.label}>
                        <FaGlobeAmericas style={styles.labelIcon} />
                        UF <span style={{color: '#e74c3c'}}>*</span>
                      </label>
                      <input
                        type="text"
                        value={formCliente.uf}
                        onChange={(e) => setFormCliente({...formCliente, uf: e.target.value.toUpperCase()})}
                        placeholder="UF"
                        maxLength="2"
                        style={{...styles.input, ...destaquePrint('uf'), ...getInputBorderStyle(null, errors.uf), textTransform: 'uppercase'}}
                      />
                      {errors.uf && <span style={styles.errorText}>{errors.uf}</span>}
                    </div>
                  </div>

                  {/* Linha 2: Logradouro | Número */}
                  <div style={styles.rowInputs}>
                    <div style={{...styles.inputGroupHalf, flex: 3}}>
                      <label style={styles.label}>
                        <FaRoad style={styles.labelIcon} />
                        Logradouro <span style={{color: '#e74c3c'}}>*</span>
                      </label>
                      <input
                        type="text"
                        value={formCliente.logradouro}
                        onChange={(e) => setFormCliente({...formCliente, logradouro: e.target.value})}
                        placeholder="Rua, Avenida..."
                        style={{...styles.input, ...destaquePrint('logradouro'), ...getInputBorderStyle(null, errors.logradouro)}}
                      />
                      {errors.logradouro && <span style={styles.errorText}>{errors.logradouro}</span>}
                    </div>

                    <div style={{...styles.inputGroupHalf, flex: 1}}>
                      <label style={styles.label}>
                        <FaHome style={styles.labelIcon} />
                        Número
                      </label>
                      <input
                        type="text"
                        value={formCliente.numero}
                        onChange={(e) => setFormCliente({...formCliente, numero: e.target.value})}
                        placeholder="Número"
                        style={{...styles.input, ...destaquePrint('numero'), ...getInputBorderStyle(null, errors.numero)}}
                      />
                      {errors.numero && <span style={styles.errorText}>{errors.numero}</span>}
                    </div>
                  </div>

                  {/* Complemento */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      <FaHome style={styles.labelIcon} />
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={formCliente.complemento}
                      onChange={(e) => setFormCliente({...formCliente, complemento: e.target.value})}
                      placeholder="Apartamento, bloco, sala..."
                      style={{...styles.input, ...destaquePrint('complemento')}}
                    />
                  </div>

                  {/* Bairro */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      <FaMapPin style={styles.labelIcon} />
                      Bairro <span style={{color: '#e74c3c'}}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formCliente.bairro}
                      onChange={(e) => setFormCliente({...formCliente, bairro: e.target.value})}
                      placeholder="Bairro"
                      style={{...styles.input, ...destaquePrint('bairro'), ...getInputBorderStyle(null, errors.bairro)}}
                    />
                    {errors.bairro && <span style={styles.errorText}>{errors.bairro}</span>}
                  </div>

                </div>

                <div style={styles.modalFooter}>
                  <button onClick={fecharModal} style={styles.cancelButton}>
                    <FaTimes /> Cancelar
                  </button>
                  <button onClick={salvarCliente} style={styles.saveButton} disabled={loading}>
                    <FaSave /> {loading ? 'Salvando...' : (modalEdicao ? 'Atualizar' : 'Salvar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to   { transform: translateY(-50%) rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

// ── Estilos ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    display: 'flex', flexDirection: 'column', minHeight: '100vh',
    backgroundColor: '#f5f5f5', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: { padding: '16px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 4px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', marginBottom: '14px'
  },
  titleContainer: { display: 'flex', alignItems: 'center', gap: '10px' },
  cnpjRow: { display: 'flex', gap: '6px', alignItems: 'stretch' },
  cnpjButton: {
    backgroundColor: '#3498db', color: '#fff', border: 'none', padding: '0 14px',
    borderRadius: '6px', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center',
  },
  cnpjButtonDisabled: { backgroundColor: '#b8c2cc', cursor: 'not-allowed' },
  titleIcon: { fontSize: '22px', color: '#3498db' },
  title: { fontSize: '20px', margin: 0, color: '#333', fontWeight: '600' },
  formContainer: {
    backgroundColor: 'white', padding: '16px', borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  filtrosContainer: {
    marginBottom: '14px', padding: '10px 12px', backgroundColor: '#f8f9fa',
    borderRadius: '8px', border: '1px solid #e9ecef'
  },
  filtrosWrapper: {
    display: 'flex', gap: '10px', alignItems: 'flex-end',
    flexWrap: 'wrap', justifyContent: 'space-between'
  },
  filtroGroupBusca: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '220px' },
  filtroLabel: { fontSize: '12px', fontWeight: '600', color: '#495057', display: 'flex', alignItems: 'center', gap: '6px' },
  filtroIcon: { fontSize: '12px', color: '#6c757d' },
  filtroInputBusca: {
    padding: '7px 10px', border: '1px solid #dee2e6', borderRadius: '6px',
    fontSize: '13px', width: '100%', outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box'
  },
  filtroActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  filtrarButton: {
    padding: '7px 14px', backgroundColor: '#3498db', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '13px', fontWeight: '600', transition: 'background-color 0.3s', whiteSpace: 'nowrap'
  },
  limparButton: {
    padding: '7px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '13px', fontWeight: '600', transition: 'background-color 0.3s', whiteSpace: 'nowrap'
  },
  addButtonInline: {
    padding: '7px 14px', backgroundColor: '#27ae60', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '13px', fontWeight: '600', transition: 'background-color 0.3s', whiteSpace: 'nowrap'
  },
  cardsContainer: { minHeight: '300px' },
  cardsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '12px', marginBottom: '14px'
  },
  card: {
    backgroundColor: 'white', borderRadius: '10px', padding: '12px 14px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e9ecef',
    transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column'
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '8px', position: 'relative'
  },
  cardIconContainer: {
    width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#ebf5fb',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, border: '1px solid #d4e6f1'
  },
  cardUserIcon: { fontSize: '18px', color: '#3498db' },
  cardTitle: { flex: 1, minWidth: 0, paddingRight: '40px' },
  cardName: {
    margin: 0, fontSize: '14.5px', fontWeight: '700', color: '#2c3e50',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    marginBottom: '2px', lineHeight: '1.3'
  },
  cardId: {
    fontSize: '11px', color: '#6c757d', fontFamily: 'monospace',
    backgroundColor: '#f8f9fa', padding: '1px 8px', borderRadius: '20px',
    display: 'inline-block', fontWeight: '500'
  },
  cardEditButton: {
    position: 'absolute', top: '50%', right: '0', transform: 'translateY(-50%)', padding: '5px 9px',
    backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center',
    gap: '5px', transition: 'all 0.2s', fontWeight: '500'
  },
  cardBody: { display: 'flex', flexDirection: 'column', gap: '5px', paddingTop: '8px', borderTop: '1px solid #f1f3f4' },
  cardInfo: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12.5px', color: '#555', lineHeight: '1.35' },
  cardInfoIcon: { fontSize: '12px', color: '#3498db', minWidth: '14px', flexShrink: 0, marginTop: '2px' },
  cardInfoText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: '#2c3e50' },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px',
    padding: '10px', borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa',
    marginTop: '6px', borderRadius: '8px'
  },
  paginationButton: {
    padding: '7px 14px', backgroundColor: '#3498db', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    gap: '6px', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s'
  },
  paginationInfo: { fontSize: '13px', color: '#495057', fontWeight: '500' },
  loading: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '14px' },
  noResults: { textAlign: 'center', padding: '40px', color: '#6c757d' },
  noResultsIcon: { fontSize: '40px', color: '#bdc3c7', marginBottom: '12px' },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000
  },
  modal: {
    backgroundColor: 'white', borderRadius: '12px', width: '95%', maxWidth: '860px',
    maxHeight: '92vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)', overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 18px', borderBottom: '1px solid #e9ecef'
  },
  modalTitle: {
    margin: 0, fontSize: '17px', color: '#2c3e50',
    display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600'
  },
  modalTitleIcon: { fontSize: '18px', color: '#3498db' },
  modalClose: {
    background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer',
    color: '#6c757d', padding: '4px', display: 'flex', alignItems: 'center',
    transition: 'color 0.2s', borderRadius: '6px'
  },
  modalContent: { padding: '14px 18px', overflowY: 'auto', flex: 1 },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '10px' },

  // ── NOVO: banner de resumo de erros, fixo no topo do conteúdo do modal ──
  erroResumoBanner: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    backgroundColor: '#fdf0ed', border: '1.5px solid #f5c6bc', borderRadius: '8px',
    padding: '8px 12px', marginBottom: '10px', color: '#c0392b', fontSize: '12.5px',
  },
  erroResumoLista: {
    margin: '4px 0 0 0', paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '2px',
  },

  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#2c3e50', textTransform: 'uppercase', letterSpacing: '0.4px',
    display: 'flex', alignItems: 'center', gap: '8px',
    marginBottom: '2px', paddingBottom: '5px', borderBottom: '2px solid #3498db'
  },
  sectionIcon: { fontSize: '14px', color: '#3498db' },
  sectionDivider: { height: '1px', backgroundColor: '#e9ecef', margin: '4px 0' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '3px' },
  inputGroupHalf: { display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '160px' },
  rowInputs: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  labelRequired: { fontSize: '12px', fontWeight: '600', color: '#2c3e50' },
  label: { fontSize: '12px', fontWeight: '500', color: '#495057', display: 'flex', alignItems: 'center', gap: '6px' },
  labelIcon: { fontSize: '11px', color: '#6c757d' },
  input: {
    padding: '7px 10px', border: '1.5px solid #dee2e6', borderRadius: '6px',
    fontSize: '13px', transition: 'all 0.3s ease', outline: 'none',
    width: '100%', boxSizing: 'border-box'
  },
  fieldHint: { fontSize: '10.5px', color: '#6c757d', fontStyle: 'italic', marginTop: '1px' },
  errorText: { color: '#e74c3c', fontSize: '11px', marginTop: '1px', fontWeight: '500' },
  modalFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: '8px',
    marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #e9ecef'
  },
  cancelButton: {
    padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    gap: '6px', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s'
  },
  saveButton: {
    padding: '8px 16px', backgroundColor: '#27ae60', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    gap: '6px', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s'
  }
};

export default GerenciarClientes;
